/**
 * Fantasy Football Calculator ADP (§4.4).
 *
 * FFC does **not** send CORS headers (re-verified 2026-07-27), so this is the
 * one Tier 1 source that genuinely cannot be called from the browser and
 * therefore earns a Worker endpoint (`/api/adp`). That proxy is explicitly
 * permitted: FFC's terms allow personal and commercial use with attribution,
 * which is why it may be edge-cached (FR-76) while Tier 3 sources may not.
 *
 * When the Worker is absent — a static-only deploy, or `functions/` deleted
 * per FR-66 — this module degrades rather than throws, and the caller drops to
 * the next rung of the §4.6 ladder.
 *
 * ADP does double duty: it powers the availability model (§8.5) *and* is a
 * first-class selectable ranking source in its own right (§4.3.4), because it
 * has the cleanest terms of anything in the source list.
 */

import type { AdpData, AdpRow, PlayerUniverse, Position, RankingSource } from '../core/types.ts';
import { PlayerIndex } from '../core/matching.ts';
import { fetchWithRetry, parseJsonByContent } from './http.ts';

export const ADP_SOURCE_ID = 'ffc-adp';
export const FFC_ATTRIBUTION = 'ADP data courtesy of Fantasy Football Calculator';
export const FFC_URL = 'https://fantasyfootballcalculator.com/adp';

export type AdpFormat = 'standard' | 'ppr' | 'half-ppr';

interface FfcResponse {
  status: string;
  meta?: {
    type?: string;
    teams?: number;
    rounds?: number;
    total_drafts?: number;
    start_date?: string;
    end_date?: string;
  };
  players?: {
    player_id: number;
    name: string;
    position: string;
    team: string;
    adp: number;
    adp_formatted?: string;
    times_drafted: number;
    high?: number;
    low?: number;
    stdev: number;
    bye?: number;
  }[];
}

export interface LoadAdpOptions {
  teams: number;
  season: string;
  format: AdpFormat;
  index: PlayerIndex;
  signal?: AbortSignal;
  /** Override for local dev or a self-hosted proxy. */
  endpoint?: string;
}

/**
 * Fetch ADP through the Worker proxy.
 *
 * Fetched **once per session at startup** and never polled (§4.4). ADP moves on
 * a multi-day rolling window; polling it would buy nothing and cost FFC.
 */
export async function loadAdp(opts: LoadAdpOptions): Promise<AdpData> {
  const endpoint = opts.endpoint ?? '/api/adp';
  const url = `${endpoint}?teams=${encodeURIComponent(opts.teams)}&year=${encodeURIComponent(opts.season)}&format=${encodeURIComponent(opts.format)}`;

  const res = await fetchWithRetry(url, { signal: opts.signal, timeoutMs: 15_000 });
  const raw = await parseJsonByContent<FfcResponse>(res);

  if (raw.status && raw.status.toLowerCase() !== 'success') {
    throw new Error(`FFC returned status "${raw.status}" — ADP is current-season only.`);
  }

  const rows: AdpRow[] = (raw.players ?? []).map((p) => {
    const position = asPosition(p.position);
    const match = opts.index.match(p.name, { position, team: p.team });
    return {
      player_key: match.player_key,
      raw_name: p.name,
      position,
      team: p.team ?? null,
      adp: p.adp,
      stdev: p.stdev ?? 0,
      times_drafted: p.times_drafted ?? 0,
      bye: p.bye ?? null,
    };
  });

  return {
    source_name: 'Fantasy Football Calculator',
    source_url: FFC_URL,
    as_of_date: raw.meta?.end_date ?? new Date().toISOString().slice(0, 10),
    // FR-31: sample size drives the confidence label on every availability
    // estimate, so it is carried through rather than discarded.
    total_drafts: raw.meta?.total_drafts ?? 0,
    scoring_format: opts.format,
    teams: raw.meta?.teams ?? opts.teams,
    rows,
  };
}

/** ADP as a selectable ranking source in its own right (§4.3.4). */
export function adpRankingSource(adp: AdpData): RankingSource {
  const ranked = adp.rows
    .filter((r) => r.player_key !== null)
    .sort((a, b) => a.adp - b.adp);

  const positionCounters = new Map<Position, number>();

  return {
    source_id: ADP_SOURCE_ID,
    source_name: 'FFC ADP',
    source_url: FFC_URL,
    as_of_date: adp.as_of_date,
    scoring_format: adp.scoring_format,
    tier: 1,
    kind: 'adp',
    unmatched: [],
    rows: ranked.map((row, i) => {
      const pos = row.position;
      let positionRank: number | null = null;
      if (pos) {
        positionRank = (positionCounters.get(pos) ?? 0) + 1;
        positionCounters.set(pos, positionRank);
      }
      return {
        source_id: ADP_SOURCE_ID,
        player_key: row.player_key,
        raw_name: row.raw_name,
        overall_rank: i + 1,
        position_rank: positionRank,
        tier: null,
        team: row.team,
        position: row.position,
        notes: `ADP ${row.adp.toFixed(1)} (${row.times_drafted.toLocaleString()} drafts)`,
      };
    }),
  };
}

/**
 * Backfill bye weeks into the player universe from ADP.
 *
 * Sleeper's `/players/nfl` payload carries no bye week (verified 2026-07-27 —
 * see NOTES.md), and FR-8 requires bye weeks on the board while FR-26 needs
 * them for the conflict penalty. FFC supplies one per player, so this is where
 * they come from. Mutates in place because the universe is a large map held
 * once and shared everywhere.
 */
export function backfillByeWeeks(universe: PlayerUniverse, adp: AdpData): number {
  let filled = 0;
  for (const row of adp.rows) {
    if (!row.player_key || !row.bye) continue;
    const player = universe.get(row.player_key);
    if (!player || player.bye_week !== null) continue;
    player.bye_week = row.bye;
    filled++;
  }
  return filled;
}

/** Choose the ADP format that best matches the league's scoring. */
export function adpFormatFor(pprValue: number): AdpFormat {
  if (pprValue >= 0.9) return 'ppr';
  if (pprValue >= 0.4) return 'half-ppr';
  return 'standard';
}

function asPosition(value: string | null | undefined): Position | null {
  if (!value) return null;
  const v = value.toUpperCase();
  if (v === 'DST' || v === 'DEF') return 'DEF';
  if (v === 'PK') return 'K';
  return (['QB', 'RB', 'WR', 'TE', 'K', 'DEF'] as const).includes(v as never)
    ? (v as Position)
    : null;
}
